// Crew Mode Charter D-4 shared identity mechanism. Verifies an operator PIN
// against a `pbkdf2:<iterations>:<digest>:<salt>:<hash>` string produced by
// packages/api/src/services/OperatorPinService.ts, using the Web Crypto API
// so this runs identically offline in the browser — no network round-trip,
// no Node-only crypto module. PBKDF2 was chosen server-side specifically
// because both Node's `crypto` and the browser's `SubtleCrypto` implement
// it natively and agree on output for the same salt/iterations/digest.

const PIN_HASH_ALGORITHM = 'pbkdf2'

const base64UrlToBytes = (value: string): Uint8Array => {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/')
  const withPadding = padded + '='.repeat((4 - (padded.length % 4)) % 4)
  const binary = atob(withPadding)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  // Not a true constant-time comparison (JS can't guarantee that at this
  // layer), but this is attribution among already-trusted co-workers on an
  // already-authenticated device, not an account-security boundary — see
  // the comment on OperatorPinService.ts. A best-effort full-length compare
  // (no early exit) is proportionate here.
  let diff = 0
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i] ^ b[i]
  }
  return diff === 0
}

const digestNameFor = (digest: string): string | null => {
  if (digest === 'sha256') return 'SHA-256'
  if (digest === 'sha1') return 'SHA-1'
  if (digest === 'sha512') return 'SHA-512'
  return null
}

type DecodedOperatorPinHash = {
  iterations: number
  hashName: string
  salt: Uint8Array
  derivedKey: Uint8Array
}

const decodeOperatorPinHash = (value: string): DecodedOperatorPinHash | null => {
  const [algorithm, iterationsValue, digest, saltValue, derivedKeyValue] = value.split(':')
  if (algorithm !== PIN_HASH_ALGORITHM || !digest || !saltValue || !derivedKeyValue) return null

  const iterations = Number.parseInt(iterationsValue ?? '', 10)
  if (!Number.isFinite(iterations) || iterations <= 0) return null

  const hashName = digestNameFor(digest)
  if (!hashName) return null

  try {
    return {
      iterations,
      hashName,
      salt: base64UrlToBytes(saltValue),
      derivedKey: base64UrlToBytes(derivedKeyValue)
    }
  } catch {
    return null
  }
}

/**
 * Verifies a candidate 4-digit PIN against a stored `pbkdf2:...` hash,
 * entirely offline. Returns false (never throws) for a malformed hash or a
 * non-matching PIN — callers should treat both identically (PIN rejected).
 */
export const verifyOperatorPinHash = async (pin: string, storedHash: string | null | undefined): Promise<boolean> => {
  if (!storedHash) return false

  const decoded = decodeOperatorPinHash(storedHash)
  if (!decoded) return false

  try {
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(pin),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    )

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: decoded.salt as BufferSource,
        iterations: decoded.iterations,
        hash: decoded.hashName
      },
      key,
      decoded.derivedKey.length * 8
    )

    return bytesEqual(new Uint8Array(derivedBits), decoded.derivedKey)
  } catch {
    return false
  }
}
